// hooks/useFollow.ts — reactive hook for follow state of a single handle.
//
// Reads from followStore reactively. Exposes toggleFollow.
// Components must NOT import followStore or followsService directly.

import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useFollowStore, normHandle } from '@/core/stores/followStore';
import { followsService } from '@/core/services/followsService';

export interface FollowHook {
  /** True when the current user follows this handle. */
  isFollowing: boolean;
  /** True when the current user has a pending follow request to this
   * (private) handle — mutually exclusive with isFollowing. */
  isRequested: boolean;
  /** Toggle follow/request state. Returns the new {following, requested} pair. */
  toggleFollow: () => Promise<{ following: boolean; requested: boolean }>;
  /**
   * Returns +1 if currently following, -1 if not.
   * Useful for computing a live followerCount delta.
   */
  followerCountDelta: 1 | -1;
}

/** Follow-button label + variant for the 3-state Follow/Requested/Following
 * idiom used everywhere a follow button renders. Centralised so every call
 * site (ProfileActions, WhoToFollowPanel, PostMoreMenu, etc.) stays in sync. */
export function followButtonLabel(isFollowing: boolean, isRequested: boolean): string {
  if (isFollowing) return 'Following';
  if (isRequested) return 'Requested';
  return 'Follow';
}

/** Follow-button variant companion to followButtonLabel — Requested reads as
 * a secondary/ghost state, same as Following, so a pending request doesn't
 * look identical to a fresh, unactioned "Follow" button. */
export function followButtonVariant(
  isFollowing: boolean,
  isRequested: boolean,
): 'secondary' | 'outline' {
  return isFollowing || isRequested ? 'secondary' : 'outline';
}

/**
 * Returns the set of normalised handles the current user follows.
 * useShallow stabilises the Object.keys array so re-renders only fire when the
 * key set changes; useMemo then keeps the returned Set instance stable across
 * renders where the key set is unchanged (preventing unnecessary downstream memos
 * — e.g. HomePage's visible-feed memo — from invalidating).
 */
export function useFollowedHandles(): Set<string> {
  const handles = useFollowStore(useShallow((s) => Object.keys(s.follows)));
  return useMemo(() => new Set(handles), [handles]);
}

/**
 * The companion set for PENDING OUTGOING requests to private accounts. Same
 * shape and same stabilisation as useFollowedHandles — for list surfaces that
 * render many rows and would otherwise call useFollow() once per row just to
 * learn that one of them is "Requested".
 */
export function useRequestedHandles(): Set<string> {
  const handles = useFollowStore(useShallow((s) => Object.keys(s.requested)));
  return useMemo(() => new Set(handles), [handles]);
}

export function useFollow(handle: string): FollowHook {
  const isFollowing = useFollowStore((s) => s.follows[normHandle(handle)]) === true;
  const isRequested = useFollowStore((s) => s.requested[normHandle(handle)]) === true;

  return {
    isFollowing,
    isRequested,
    toggleFollow: () => followsService.toggleFollow(handle),
    followerCountDelta: isFollowing ? 1 : -1,
  };
}

/**
 * Syncs the follow/request state for a single handle from the server on
 * mount (and whenever `handle` changes), when `enabled`. Fixes "Requested"
 * going permanently stale once the OTHER side accepts: the boot-time
 * hydration lists only cover handles that were already following/pending at
 * sign-in, so a profile visited later never otherwise re-checks truth.
 *
 * `followsService.isFollowing` already exists and writes both flags into the
 * store — this is its only call site. Scoped to profile-mount (not folded
 * into useFollow itself) so the many list-row surfaces that call useFollow
 * per-row don't each fire their own network request.
 */
export function useSyncFollowState(handle: string | undefined, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || !handle) return;
    void followsService.isFollowing(handle).catch(() => {});
  }, [handle, enabled]);
}
